import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Send, CheckCircle2, User } from 'lucide-react';
import ticketsService from '@/services/tickets.service';
import useAuthStore from '@/stores/authStore';
import RichTextEditor from '@/components/RichTextEditor';
import i18n from '@/i18n';
import { bcp47Of } from '@/i18n/languages';

const fmtDate = (s?: string) =>
  s ? new Intl.DateTimeFormat(bcp47Of(i18n.language || 'de'), { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(s)) : '';

export default function WarehouseTicket() {
  const { t } = useTranslation('warehouse');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [reply, setReply] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['warehouse-ticket', id],
    queryFn: () => ticketsService.getTicketById(id),
    enabled: !!id,
  });
  const me = useAuthStore((st) => st.user);
  const ticket = data?.data?.ticket;
  const isInternalConv = !!(ticket as any)?.internal_target_org_id;
  const comments = (data?.data?.comments || []).filter((c) => c.is_public !== false);

  const sendM = useMutation({
    mutationFn: () => ticketsService.replyToTicket(id, { content: reply, sendEmail: true }),
    onSuccess: () => {
      toast.success(t('ticket.replySent', 'Antwort gesendet'));
      setReply('');
      qc.invalidateQueries({ queryKey: ['warehouse-ticket', id] });
      qc.invalidateQueries({ queryKey: ['warehouse-tickets'] });
    },
    onError: () => toast.error(t('ticket.sendFailed', 'Senden fehlgeschlagen')),
  });
  const doneM = useMutation({
    mutationFn: () => ticketsService.updateTicket(id, { status: 'solved' } as any),
    onSuccess: () => { toast.success(t('ticket.markedDone', 'Als erledigt markiert')); qc.invalidateQueries({ queryKey: ['warehouse-tickets'] }); navigate('/warehouse'); },
    onError: () => toast.error(t('ticket.failed', 'Fehlgeschlagen')),
  });

  const plainEmpty = !reply.replace(/<[^>]*>/g, '').trim();

  return (
    <div>
      <button onClick={() => navigate('/warehouse')} className="mb-3 flex items-center gap-1.5 rounded-xl bg-white px-3.5 py-2.5 text-base font-bold text-slate-700 shadow-sm active:scale-95">
        <ArrowLeft className="h-5 w-5" /> {t('ticket.back', 'Zurück')}
      </button>

      {isLoading ? (
        <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : !ticket ? (
        <div className="py-24 text-center text-slate-400">{t('ticket.notFound', 'Nicht gefunden')}</div>
      ) : (
        <>
          <div className="mb-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xl font-extrabold leading-snug text-slate-800">{ticket.subject || t('ticket.noSubject', '(kein Betreff)')}</div>
            <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
              <User className="h-4 w-4" />
              {[ticket.requester_first_name, ticket.requester_last_name].filter(Boolean).join(' ') || ticket.requester_email}
            </div>
          </div>

          {/* Konuşma */}
          <div className="space-y-3">
            {comments.length === 0 && ticket.description && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[15px] leading-relaxed text-slate-700">
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ticket.description) }} />
              </div>
            )}
            {comments.map((c) => {
              // Interne Konversation: 'gelen' = başkasının yazdığı (karşı org) — kendi mesajın mavi, karşınınki beyaz+isimli
              const incoming = isInternalConv
                ? ((c as any).author_id !== (me as any)?.id)
                : (c.email_source_type === 'incoming' || c.author_role === 'customer' || c.metadata?.external_party === true);
              const who = isInternalConv
                ? ([c.author_first_name, c.author_last_name].filter(Boolean).join(' ') || t('ticket.message', 'Nachricht'))
                : (incoming ? t('ticket.customer', 'Kunde') : ([c.author_first_name, c.author_last_name].filter(Boolean).join(' ') || t('ticket.reply', 'Antwort')));
              return (
                <div key={c.id} className={`rounded-2xl border p-4 text-[15px] leading-relaxed ${incoming ? 'border-slate-200 bg-white text-slate-700' : 'border-[#0F99FD]/25 bg-[#0F99FD]/[0.05] text-slate-700'}`}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className={`text-xs font-bold uppercase ${incoming ? 'text-slate-500' : 'text-[#0F99FD]'}`}>{who}</span>
                    <span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span>
                  </div>
                  <div className="break-words [&_a]:text-[#0F99FD]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.content || '') }} />
                </div>
              );
            })}
          </div>

          {/* Cevap */}
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 text-base font-bold text-slate-700">{t('ticket.replyTitle', 'Antwort schreiben')}</div>
            <RichTextEditor value={reply} onChange={setReply} placeholder={t('ticket.replyPlaceholder', 'Ihre Antwort an den Kunden…')} />
            <button
              onClick={() => sendM.mutate()}
              disabled={sendM.isPending || plainEmpty}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0F99FD] py-4 text-lg font-extrabold text-white shadow-md transition active:scale-[0.99] disabled:opacity-50"
            >
              {sendM.isPending ? <Loader2 className="h-6 w-6 animate-spin" /> : <Send className="h-6 w-6" />} {t('ticket.sendReply', 'Antwort senden')}
            </button>
          </div>

          <button
            onClick={() => doneM.mutate()}
            disabled={doneM.isPending}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-emerald-500 bg-emerald-50 py-3.5 text-base font-bold text-emerald-700 active:scale-[0.99] disabled:opacity-50"
          >
            <CheckCircle2 className="h-5 w-5" /> {t('ticket.markDone', 'Als erledigt markieren')}
          </button>
        </>
      )}
    </div>
  );
}
